<template>
  <div>
    <b-card>
      <div class="organism-backup_header card-padding">
        <span class="header__label">Backup Management</span>
        <atom-button
          v-if="hasUpdatePermission"
          buttonId="create-new-backup"
          class="header__button"
          buttonStyle="border"
          :isDisable="!currentVeeamId || !getVeeamStatus"
          @click="onClickCreateNewBackup"
        >
          <div class="d-flex d-md-none">Create</div>
          <div class="d-none d-md-flex">Create New Backup</div>
        </atom-button>
      </div>

      <div class="filter-container card-padding pt-0">
        <div class="row">
          <div class="col-12 col-lg-4 d-flex">
            <atom-input
              v-model="filterList.name"
              title="Name"
              placeholder="Name"
              :isDisable="!currentVeeamId"
            ></atom-input>
          </div>
          <div class="col-12 col-lg-4 d-flex mt-1 mt-lg-0">
            <atom-select
              v-model="filterList.status"
              title="Status"
              placeholder="All Status"
              label="name"
              :isAppendToBody="false"
              :isEnableProper="false"
              :option="statusOption"
              :isDisable="!currentVeeamId"
            ></atom-select>
          </div>
        </div>

        <div class="row mt-1">
          <div class="col-12 col-lg-8 d-flex justify-content-end">
            <atom-select
              v-model="filterList.lastResult"
              title="Last Result"
              placeholder="All Last Result"
              label="name"
              :option="resultOption"
              :isAppendToBody="false"
              :isEnableProper="false"
              :isDisable="!currentVeeamId"
            ></atom-select>
            <div class="d-flex align-items-end">
              <atom-button
                buttonId="button-search"
                iconLeft="SearchIcon"
                class="search__button ml-2"
                :isDisable="!currentVeeamId || !getVeeamStatus"
                @click="clickButtonSearch"
              ></atom-button>
              <div
                class="reset-filter-button ml-1"
                :class="{ 'disabled-button': !currentVeeamId }"
                @click="clickButtonClearFilter"
              >
                <span>Reset</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row mt-3">
        <div class="col-12">
          <b-table
            sticky-header
            responsive
            :items="items"
            :fields="hasUpdatePermission ? fields : fields.filter(field => field.key !== 'deleteIcon')"
            :hover="true"
            @row-clicked="selectRow"
          >
            <template #cell(status)="data">
              <b-badge
                pill
                :variant="badgeVariant(data.value)"
                class="badge--width"
                >{{ badgeStatus(data.value) }}</b-badge
              >
            </template>
            <template #cell(name)="data">
              <div class="header">{{ data.value }}</div>
            </template>
            <template #cell(lastRun)="data">
              <div>
                {{ data.value.date || "-" }}
                <feather-icon
                  v-if="data.value.result === 'FAILED'"
                  icon="XIcon"
                  size="15"
                  class="icon_red"
                />
                <feather-icon
                  v-if="data.value.result === 'SUCCESS'"
                  icon="CheckIcon"
                  size="15"
                  class="icon-success"
                />
                <feather-icon
                  v-if="data.value.result === 'WARNING'"
                  icon="AlertCircleIcon"
                  size="15"
                  class="icon-warning"
                />
              </div>
              <div class="sub-header">{{ data.value.time }}</div>
            </template>
            <template #cell(nextRun)="data">
              <div>{{ data.value.date || "-" }}</div>
              <div class="sub-header">{{ data.value.time }}</div>
            </template>
            <template #cell(deleteIcon)="data">
              <atom-button
                buttonId="button-delete"
                iconLeft="Trash2Icon"
                buttonStyle="flat"
                buttonColor="secondary"
                @click="clickDeleteBackup(data)"
              ></atom-button>
            </template>
          </b-table>
        </div>
        <div
          v-if="!currentVeeamId"
          class="w-100 d-flex flex-column justify-content-center align-items-center p-4"
        >
          <feather-icon
            icon="CloudOffIcon"
            class="empty-data__icon"
          ></feather-icon>
          <label class="empty-data__text--title">No Data</label>
          <label v-if="!currentVeeamId" class="empty-data__text--body"
            >Please Select Veeam</label
          >
        </div>
      </div>
      <div class="row card-padding">
        <div class="col-6">Showing {{ items.length }} entries</div>
        <div class="col-6 d-flex justify-content-end">
          <div
            v-if="filterList.totalCount"
            class="col-6 d-flex justify-content-end"
          >
            <b-pagination
              v-model="filterList.currentPage"
              :total-rows="filterList.total"
              :per-page="perPage"
              size="sm"
              class="my-0"
            ></b-pagination>
          </div>
        </div>
      </div>
    </b-card>

    <molecule-modal-dialog
      :modalId="modalDeleteId"
      :isTextCenter="true"
      icon="AlertCircleIcon"
      textTitle="Do you want to delete?"
      :isSlotBody="true"
      buttonTextLeft="Delete"
      buttonTextRight="Cancel"
      buttonColorLeft="danger"
      @clickButtonLeft="clickConfirmDelete"
    >
      <template #body>
        <div>
          Backup:
          <span class="header">{{
            currentDelete.name ? currentDelete.name : "-"
          }}</span>
        </div>
      </template>
    </molecule-modal-dialog>
    <footer style="height: 2px"></footer>
  </div>
</template>

<script>
import { BCard, BTable, BBadge, BPagination } from "bootstrap-vue"
import AtomButton from "@/components/atoms/AtomButton.vue"
import AtomInput from "@/components/atoms/AtomInput.vue"
import AtomMultiSelect from "@/components/atoms/AtomMultiSelect.vue"
import AtomSelect from "@/components/atoms/AtomSelect.vue"
import MoleculeModalDialog from "@/components/molecules/MoleculeModalDialog.vue"
import { mapGetters } from "vuex"
import { PermissionUser } from "@/constants/permission-user"
import mixinsUser from "@/mixins/mixinsUser"

export default {
  name: "OrganismBackupManagement",
  components: {
    BCard,
    BTable,
    BPagination,
    BBadge,
    AtomButton,
    AtomInput,
    AtomMultiSelect,
    AtomSelect,
    MoleculeModalDialog,
  },
  props: {
    currentVeeamId: {
      type: String,
      required: false,
      default: null,
    },
    filterList: {
      required: true,
      type: Object,
    },
    statusOption: {
      required: true,
      type: Array,
    },
    resultOption: {
      required: true,
      type: Array,
    },
    fields: {
      type: Array,
      required: false,
      default: [],
    },
    items: {
      type: Array,
      required: false,
      default: [],
    },
  },
  emits: ["delete"],
  mixins: [mixinsUser],
  data() {
    return {
      currentDelete: {},
      modalDeleteId: "modal-delete-backup",
      perPage: 10,
    }
  },
  computed: {
    ...mapGetters("app", ["getVeeamStatus"]),

    hasUpdatePermission() {
      return this.hasPermission(PermissionUser.BMM.BACKUP_WRITE)
    },
  },
  watch: {
    "filterList.currentPage": {
      deep: true,
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.$emit("onUpdateCurrentPage", this.filterList)
        }
      },
    },
  },
  methods: {
    onClickCreateNewBackup() {
      this.$router.push("/backup-management/backup/create")
    },

    badgeVariant(status) {
      if (status.toLowerCase() === "inactive") return "light-warning"
      else if (status.toLowerCase() === "running") return "light-success"
      else return "light-secondary"
    },

    badgeStatus(status) {
      if (status.toLowerCase() === "inactive") return "Inactive"
      else if (status.toLowerCase() === "running") return "Running"
      else return "Disabled"
    },

    clickDeleteBackup(data) {
      this.currentDelete = data.item
      this.$bvModal.show(this.modalDeleteId)
    },

    clickConfirmDelete() {
      this.$bvModal.hide(this.modalDeleteId)

      this.$emit("delete", this.currentDelete.id)

      this.currentDelete = {}
    },

    selectRow(record) {
      this.$router.push(`/backup-management/backup/detail/${record.id}`)
    },

    clickButtonSearch() {
      this.$emit("onUpdateFilterData", this.filterList)
    },

    clickButtonClearFilter() {
      if (this.currentVeeamId) {
        this.$emit("onSetDefaultFilterData")
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.organism-backup_header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header__label {
  font-weight: 500;
  font-size: 18px;
  color: #5e5873;
}

.search__button {
  margin-top: 23px;
  margin-right: 0;
  height: fit-content;
}

.card-body {
  padding: 0;
}

.card-padding {
  padding: 1.5rem;
}

.filter-container {
  border-bottom: 1px solid #e9ecef;
}

.header {
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: #6e6b7b;

  overflow-wrap: break-word;
  word-break: break-word;
}

.sub-header {
  font-weight: 400;
  font-size: 12px;
  color: #b9b9c3;
}

.icon_red {
  color: #ea5455;
}

.icon-warning {
  color: #ffce43;
}

.icon-success {
  color: #28c76f;
}

.empty-data__icon {
  width: 100px;
  height: 100px;
}

.empty-data__text--title {
  color: #5e5873;
  font-weight: 500;
  font-size: 18px;
  margin-top: 20px;
}

.empty-data__text--body {
  color: #b9b9c3;
  font-size: 14px;
  margin-top: 10px;
}

.reset-filter-button {
  color: #b9b9c3;
  margin-bottom: 8px;
  text-decoration: underline;
  &:not(.disabled-button):hover {
    cursor: pointer;
    color: #7367f0;
  }
}

@media screen and (min-width: 576px) {
  .organism-backup_header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}

// Medium devices (tablets, 768px and up)
@media screen and (min-width: 768px) {
}

// Large devices (desktops, 992px and up)
@media screen and (min-width: 992px) {
}

// X-Large devices (large desktops, 1200px and up)
@media screen and (min-width: 1200px) {
}
</style>
